import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getLatLong } from '../common/helpers';
import { dinero } from '../../utils/dinero';

const containerStyle = { width: '100%', height: '300px', borderRadius: '12px' };
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 }; // Centro de México

// Cache de geocodificación por dirección (persiste durante la sesión)
const geoCache = new Map();

// getLatLong siempre contesta (null si Google no encuentra la dirección, falla o tarda más de 5 s); aquí se recuerda la respuesta
const geocodeSafe = (dir) => {
  if (geoCache.has(dir)) return Promise.resolve(geoCache.get(dir));
  return getLatLong(dir).then(coords => {
    geoCache.set(dir, coords);
    return coords;
  });
};

// Coordenadas guardadas en los servicios de la dirección (GeoJSON [lng, lat]; las manda la búsqueda, como al mapa del inicio).
// Si ninguno las tiene, el pin se ubica geocodificando la dirección.
const coordsGuardadas = (items) => {
  for (const s of items) {
    const c = s.location?.coordinates;
    if (Array.isArray(c) && c.length === 2 && c.every(Number.isFinite)) return { lat: c[1], lng: c[0] };
  }
  return null;
};

const SearchMap = ({ servicios = [] }) => {
  // Los marcadores del mapa van con style (los pinta Google Maps): llevan los valores del tema, no sus nombres
  const { palette } = useTheme();
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));
  const mapRef = useRef(null);

  // El script de Google Maps se carga en index.html; esperar a que esté listo antes de montar el mapa
  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 300);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Clave estable: solo cambia cuando cambian las direcciones únicas (no al ordenar/filtrar precio)
  const direccionesUnicas = [...new Set(
    servicios.map(s => (s.ubicacion || '').trim()).filter(Boolean)
  )];
  const dirKey = [...direccionesUnicas].sort().join('|');

  useEffect(() => {
    let cancelled = false;

    const porDireccion = {};
    servicios.forEach(s => {
      const dir = (s.ubicacion || '').trim();
      if (!dir) return;
      if (!porDireccion[dir]) porDireccion[dir] = [];
      porDireccion[dir].push(s);
    });

    // Las direcciones con coordenadas guardadas entran todas (no cuestan nada); las demás se geocodifican, hasta 30
    // (cada una es una llamada a Google)
    const todas = Object.keys(porDireccion).map(dir => ({
      direccion: dir, items: porDireccion[dir], coords: coordsGuardadas(porDireccion[dir]),
    }));
    const porGeocodificar = new Set(todas.filter(g => !g.coords).slice(0, 30));
    const direcciones = todas.filter(g => g.coords || porGeocodificar.has(g));
    if (direcciones.length === 0) {
      setMarkers([]);
      setLoading(false);
      return;
    }

    if (porGeocodificar.size > 0) setLoading(true);
    Promise.all(direcciones.map(async (g) => {
      const coords = g.coords || await geocodeSafe(g.direccion);
      return coords ? { coords, direccion: g.direccion, items: g.items } : null;
    })).then(res => {
      if (cancelled) return;
      setMarkers(res.filter(Boolean));
      setActive(null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [dirKey]);

  // Ajustar el encuadre del mapa a todos los marcadores
  useEffect(() => {
    if (!mapRef.current || !window.google || markers.length === 0) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend(m.coords));
      mapRef.current.fitBounds(bounds);
      if (markers.length === 1) mapRef.current.setZoom(15);
    } catch (e) { /* noop */ }
  }, [markers, mapsReady]);

  return (
    <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', mb: 4, border: 1, borderColor: 'sutil' }}>
      {mapsReady ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={DEFAULT_CENTER}
          zoom={5}
          onLoad={(map) => { mapRef.current = map; }}
          onUnmount={() => { mapRef.current = null; }}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {markers.map((m, i) => (
            <OverlayView key={i} position={m.coords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div
                style={{ transform: 'translate(-50%, -100%)', cursor: 'pointer', position: 'relative' }}
                onClick={() => setActive(active === i ? null : i)}
              >
                <LocationOnIcon style={{ color: palette.primary.main, fontSize: 38, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }} />
                {active === i && (
                  <div style={{
                    position: 'absolute', bottom: 42, left: '50%', transform: 'translateX(-50%)',
                    background: 'white', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    padding: '10px 12px', width: 210, zIndex: 1000
                  }}>
                    {m.items.slice(0, 4).map(s => (
                      <Typography key={s._id} variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'primary.main' }}>
                        {s.nombre} — {dinero(s.costoServicio)}
                      </Typography>
                    ))}
                    {m.items.length > 4 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        +{m.items.length - 4} servicio(s) más
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      {m.direccion}
                    </Typography>
                  </div>
                )}
              </div>
            </OverlayView>
          ))}
        </GoogleMap>
      ) : (
        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
          <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
        </Box>
      )}

      {loading && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', borderRadius: '50%', p: 0.6, boxShadow: 1 }}>
          <CircularProgress size={18} sx={{ color: 'secondary.main' }} />
        </Box>
      )}

      {mapsReady && !loading && markers.length === 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.75)', pointerEvents: 'none', textAlign: 'center', px: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            Ningún servicio de la búsqueda tiene una dirección para mostrar en el mapa.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchMap;
